import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../../../types';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../components/Toast';
import { userService } from '../../../services/userService';

interface EditProfileDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    user: User | null;
    onSuccess: () => void;
}

export const EditProfileDialog: React.FC<EditProfileDialogProps> = ({ open, onOpenChange, user, onSuccess }) => {
    const { t } = useTranslation(['profile', 'common']);
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        bio: '',
        password: '',
        confirmPassword: '',
    });
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    useEffect(() => {
        if (user && open) {
            setFormData({
                full_name: user.full_name || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || '',
                bio: user.bio || '',
                password: '',
                confirmPassword: '',
            });
            setAvatarPreview(null);
            setAvatarFile(null);
        }
    }, [user, open]);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showToast(t('edit.fileSizeError'), 'error');
                return;
            }
            if (!file.type.startsWith('image/')) {
                showToast(t('edit.fileTypeError'), 'error');
                return;
            }
            setAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (formData.password) {
                if (formData.password.length < 8) {
                    showToast(t('edit.passwordLengthError'), 'error');
                    setLoading(false);
                    return;
                }
                if (formData.password !== formData.confirmPassword) {
                    showToast(t('edit.passwordMatchError'), 'error');
                    setLoading(false);
                    return;
                }
            }

            let avatarUrl = user?.avatar;
            if (avatarFile && user) {
                const uploadedUser = await userService.uploadAvatar(user.id, avatarFile);
                avatarUrl = uploadedUser.avatar;
            }

            const updatePayload: any = {
                full_name: formData.full_name,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                bio: formData.bio,
            };

            if (avatarUrl) {
                updatePayload.avatar = avatarUrl;
            }

            if (formData.password) {
                updatePayload.password = formData.password;
            }

            await userService.updateProfile(updatePayload);
            showToast(t('edit.success'), 'success');
            onSuccess();
            onOpenChange(false);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || t('edit.error');
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('edit.title')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6 py-4">
                    <div className="flex flex-col items-center gap-4">
                        <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-gray-100">
                            {avatarPreview ? (
                                <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                            ) : user?.avatar ? (
                                // Simple img tag for now or use Avatar component but this is preview
                                <img src={avatarPreview || (user.avatar ? (user.avatar.startsWith('http') ? user.avatar : `http://localhost:5000${user.avatar}`) : '')}
                                    alt="Current" className="w-full h-full object-cover"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://github.com/shadcn.png';
                                    }}
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-2xl text-gray-400">
                                    {(user?.full_name?.[0] || 'U')}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center">
                            <Label htmlFor="avatar-upload" className="cursor-pointer text-sm text-primary-600 font-medium hover:underline">
                                {t('edit.chooseAvatar')}
                            </Label>
                            <Input
                                id="avatar-upload"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleAvatarChange}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="fullName">{t('edit.fullName')}</Label>
                            <Input
                                id="fullName"
                                value={formData.full_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">{t('edit.email')}</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phone">{t('edit.phone')}</Label>
                            <Input
                                id="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="address">{t('edit.address')}</Label>
                            <Input
                                id="address"
                                value={formData.address}
                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="bio">{t('sidebar.bio', { defaultValue: 'Bio' })}</Label>
                        <Textarea
                            id="bio"
                            value={formData.bio}
                            onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                            className="resize-none"
                            rows={3}
                        />
                    </div>

                    <div className="border-t pt-4 space-y-4">
                        <h4 className="text-sm font-medium text-gray-900">Change Password</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">{t('edit.newPassword')}</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder={t('edit.passwordPlaceholder')}
                                    value={formData.password}
                                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">{t('edit.confirmPassword')}</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder={t('edit.confirmPasswordPlaceholder')}
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" type="button" onClick={() => onOpenChange(false)} disabled={loading}>
                            {t('edit.cancel')}
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? t('edit.saving') : t('edit.save')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
